// src/Pages/Hmo/PlanOwnerDetails.js
// ✅ FIXED: Dynamic premium calculation with Paystack integration + CORRECT navigation
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../../utils/api';

function PlanOwnerDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [paystackLoaded, setPaystackLoaded] = useState(false);  // ✅ Paystack SDK state
  const [productLoading, setProductLoading] = useState(true);  // ✅ Product fetch loading

  // ✅ FSL Brand Colors - ORANGE
  const PRIMARY_COLOR = '#F89423';
  const PRIMARY_HOVER = '#E0851F';
  const INFO_BG = '#FFF8F0';
  const INFO_BORDER = '#FDE4C8';
  const INFO_TEXT = '#C2410C';
  const INFO_ICON = '#F89423';
  const PREMIUM_BG = '#F0FDF4';
  const PREMIUM_TEXT = '#166534';

  // ✅ Extract params including agent attribution
  const companyId = searchParams.get('company');
  const linkId = searchParams.get('link');
  const productId = searchParams.get('product');
  const agentId = searchParams.get('agent');

  const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';

  // ✅ Paystack Config
  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_f4d75082b401a553cdfb9ed7eb11ae2a7be9d099';

  // ✅ Gender options state (fetched from backend)
  const [genderOptions, setGenderOptions] = useState([]);
  const [gendersLoading, setGendersLoading] = useState(true);

  // ✅ Personal Details State
  const [personalDetails, setPersonalDetails] = useState({
    personalName: '',
    personalEmail: '',
    personalPhone: '',
    dateOfBirth: '',
    gender: ''
  });

  // ✅ Spouse Details State (optional)
  const [hasSpouse, setHasSpouse] = useState(false);
  const [spouseDetails, setSpouseDetails] = useState({
    spouseName: '',
    spouseEmail: '',
    spousePhone: '',
    spouseDateOfBirth: '',
    spouseGender: ''
  });

  // ✅ Dependents State - Multiple dependents support
  const [dependents, setDependents] = useState([
    { id: Date.now(), name: '', gender: '', relationship: '' }
  ]);

  // ✅ NEW: Product & Premium State
  const [productDetails, setProductDetails] = useState(null);
  const [basePremiumPerMonth, setBasePremiumPerMonth] = useState(30000);  // Fallback default
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [premium, setPremium] = useState(0);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  const paymentPeriods = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `${i + 1} Month${i + 1 > 1 ? 's' : ''}`,
    months: i + 1
  }));

  // ✅ Fetch gender options from backend on mount
  useEffect(() => {
    const fetchGenders = async () => {
      try {
        setGendersLoading(true);
        
        const response = await authenticatedFetch(
          `${API_BASE}/Settings/getgenders`,
          'POST',
          {}
        );
        
        if (response?.status === 'success' && Array.isArray(response.data)) {
          setGenderOptions(response.data);
        } else {
          console.warn('⚠️ Using fallback gender options');
          setGenderOptions([
            { id: 1, name: 'Male' },
            { id: 2, name: 'Female' },
            { id: 3, name: 'Other' },
            { id: 4, name: 'Prefer not to say' }
          ]);
        }
      } catch (error) {
        console.error('💥 Error fetching genders:', error);
        setGenderOptions([
          { id: 1, name: 'Male' },
          { id: 2, name: 'Female' },
          { id: 3, name: 'Other' },
          { id: 4, name: 'Prefer not to say' }
        ]);
        toast.warning('Using default gender options');
      } finally {
        setGendersLoading(false);
      }
    };
    
    fetchGenders();
  }, []);

  // ✅ NEW: Fetch product details to get MinimumPremium
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setProductLoading(true);
        
        if (!companyId || !linkId || !productId) {
          throw new Error('Missing required parameters');
        }
        
        // ✅ Call your endpoint to get link with bundled products
        const response = await authenticatedFetch(
          `${API_BASE}/Settings/links/company/${companyId}?userType=hmo`,
          'POST',
          {}
        );
        
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to fetch product details');
        }
        
        // ✅ Find the specific link and product
        const links = response.data || [];
        const targetLink = links.find(l => l.linkId === linkId);
        
        if (!targetLink) {
          throw new Error(`Link not found: ${linkId}`);
        }
        
        const bundledProducts = targetLink.bundledProducts || [];
        const targetProduct = bundledProducts.find(p => 
          String(p.Id || p.id) === String(productId)
        );
        
        if (!targetProduct) {
          throw new Error(`Product not found: ${productId}`);
        }
        
        // ✅ Extract MinimumPremium (handle both number and string formats)
        const minPremium = targetProduct.MinimumPremium ?? targetProduct.minimumPremium;
        const premiumValue = typeof minPremium === 'number' ? minPremium : parseFloat(minPremium) || 30000;
        
        console.log('✅ Product loaded:', {
          name: targetProduct.Name || targetProduct.name,
          minimumPremium: premiumValue
        });
        
        setProductDetails(targetProduct);
        setBasePremiumPerMonth(premiumValue);
        
      } catch (error) {
        console.error('💥 Error fetching product details:', error);
        toast.warning('Using default premium. Product details could not be loaded.');
        // Keep fallback default of 30000
        setBasePremiumPerMonth(30000);
      } finally {
        setProductLoading(false);
      }
    };
    
    if (companyId && linkId && productId) {
      fetchProductDetails();
    }
  }, [companyId, linkId, productId]);

  // ✅ NEW: Load Paystack SDK
  useEffect(() => {
    if (window.PaystackPop) return setPaystackLoaded(true);

    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setPaystackLoaded(true);
    script.onerror = () => {
      toast.error('Failed to load payment gateway. Check your internet connection.');
      setPaystackLoaded(false);
    };
    document.body.appendChild(script);

    return () => {
      const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
      if (existing) existing.remove();
    };
  }, []);

  // ✅ Personal Details Handlers
  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPersonalDetails(prev => ({ ...prev, [name]: value }));
  };

  // ✅ Spouse Handlers
  const handleSpouseChange = (e) => {
    const { name, value } = e.target;
    setSpouseDetails(prev => ({ ...prev, [name]: value }));
  };

  // ✅ Dependent Handlers
  const addDependent = () => {
    setDependents(prev => [
      ...prev,
      { id: Date.now(), name: '', gender: '', relationship: '' }
    ]);
  };

  const removeDependent = (id) => {
    if (dependents.length <= 1) {
      toast.warning('At least one dependent is required');
      return;
    }
    setDependents(prev => prev.filter(d => d.id !== id));
  };

  const handleDependentChange = (id, field, value) => {
    setDependents(prev => prev.map(d => 
      d.id === id ? { ...d, [field]: value } : d
    ));
  };

  // ✅ NEW: Period selection handler with premium calculation
  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    setPremium(basePremiumPerMonth * period.months);  // ✅ Uses dynamic premium
    setShowPeriodDropdown(false);
  };

  // ✅ NEW: Format price helper
  const formatPrice = (price) => `₦${Number(price).toLocaleString()}`;

  // ✅ Validation
  const validateForm = () => {
    if (!personalDetails.personalName.trim()) {
      toast.warning('Please enter your full name');
      return false;
    }
    if (!personalDetails.personalEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalDetails.personalEmail)) {
      toast.warning('Please enter a valid email address');
      return false;
    }
    if (!personalDetails.personalPhone.trim() || personalDetails.personalPhone.replace(/\D/g, '').length < 11) {
      toast.warning('Please enter a valid phone number');
      return false;
    }
    if (!personalDetails.dateOfBirth) {
      toast.warning('Please select your date of birth');
      return false;
    }
    if (!personalDetails.gender) {
      toast.warning('Please select your gender');
      return false;
    }
    if (!selectedPeriod) {
      toast.warning('Please select a coverage period');
      return false;
    }

    if (hasSpouse) {
      if (!spouseDetails.spouseName.trim()) {
        toast.warning('Please enter spouse name');
        return false;
      }
      if (!spouseDetails.spouseGender) {
        toast.warning('Please select spouse gender');
        return false;
      }
      if (spouseDetails.spouseEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(spouseDetails.spouseEmail)) {
        toast.warning('Please enter a valid spouse email');
        return false;
      }
      if (!spouseDetails.spouseDateOfBirth) {
        toast.warning('Please select spouse date of birth');
        return false;
      }
    }

    for (const dep of dependents) {
      if (!dep.name.trim()) {
        toast.warning('Please enter dependent name');
        return false;
      }
      if (!dep.gender) {
        toast.warning('Please select dependent gender');
        return false;
      }
      if (!dep.relationship.trim()) {
        toast.warning('Please select relationship');
        return false;
      }
    }

    return true;
  };

  // ✅ NEW: Generate unique reference for Paystack
  const generateReference = () => `FSL-${Date.now()}-${Math.random().toString(36).substring(2, 11).toUpperCase()}`;

  // ✅✅✅ FIXED: Handle payment success and navigate to payment-success page ✅✅✅
  const handlePaymentSuccess = async (paystackResponse) => {
    try {
      // ✅ Prepare payload matching PlanOwnerRequest model
      const payload = {
        companyId: companyId ? parseInt(companyId) : null,
        linkId: linkId,
        productId: productId ? parseInt(productId) : null,
        agentId: agentId,
        
        personalName: personalDetails.personalName.trim(),
        personalEmail: personalDetails.personalEmail.toLowerCase().trim(),
        personalPhone: personalDetails.personalPhone.replace(/\D/g, ''),
        dateOfBirth: personalDetails.dateOfBirth,
        gender: personalDetails.gender,
        
        hasSpouse: hasSpouse,
        spouseName: hasSpouse ? spouseDetails.spouseName.trim() : null,
        spouseGender: hasSpouse ? spouseDetails.spouseGender : null,
        spouseEmail: hasSpouse && spouseDetails.spouseEmail ? spouseDetails.spouseEmail.toLowerCase().trim() : null,
        spousePhone: hasSpouse && spouseDetails.spousePhone ? spouseDetails.spousePhone.replace(/\D/g, '') : null,
        spouseDateOfBirth: hasSpouse ? spouseDetails.spouseDateOfBirth : null,
        
        dependents: dependents.map(dep => ({
          name: dep.name.trim(),
          gender: dep.gender,
          relationship: dep.relationship.trim()
        })),
        
        // ✅ Payment details
        paymentReference: paystackResponse.reference,
        transactionId: paystackResponse.tranx_id,
        amount: premium,
        currency: 'NGN',
        periodMonths: selectedPeriod?.months,
        paidAt: new Date().toISOString()
      };

      console.log('📤 Submitting plan owner details with payment:', payload);
      
      // ✅ API Call to your backend endpoint
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/save-plan-owner`,
        'POST',
        payload
      );
      
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to save plan owner details');
      }
      
      // ✅ Save to localStorage for next steps (optional backup)
      const applicantData = {
        ...payload,
        planOwnerId: response.data?.planOwnerId,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem('planOwnerDetails', JSON.stringify(applicantData));
      
      toast.success('Payment successful! Details saved.');
      
      // ✅✅✅ BUILD URL WITH ALL PAYMENT DETAILS ✅✅✅
      const queryParams = new URLSearchParams();
      
      // Payment details (critical for PaymentSuccess page)
      if (paystackResponse?.reference) queryParams.set('reference', paystackResponse.reference);
      if (premium) queryParams.set('amount', premium.toString());
      if (selectedPeriod?.label) queryParams.set('period', selectedPeriod.label);
      if (personalDetails?.personalEmail) queryParams.set('email', personalDetails.personalEmail);
      if (response.data?.planOwnerId) queryParams.set('planOwnerId', response.data.planOwnerId.toString());
      queryParams.set('timestamp', new Date().toISOString());
      
      // Context params for attribution and navigation
      if (companyId) queryParams.set('company', companyId);
      if (linkId) queryParams.set('link', linkId);
      if (productId) queryParams.set('product', productId);
      if (agentId) queryParams.set('agent', agentId);  // ✅ Critical for agent attribution
      
      const queryString = queryParams.toString();
      
      // ✅✅✅ NAVIGATE TO CORRECT ROUTE WITH PARAMS ✅✅✅
      // ✅ FIXED: Correct route name "/insurance/payment-success" (not "/paymeny-successfull")
      navigate(`/insurance/payment-success?${queryString}`, { replace: true });
      
    } catch (error) {
      console.error('💥 Error saving plan owner details:', error);
      
      if (error.message?.includes('Missing Authorization')) {
        toast.error('Authentication failed. Please log in again.');
      } else if (error.message?.includes('Invalid or expired link')) {
        toast.error('This link is no longer valid.');
      } else if (error.message?.includes('duplicate email')) {
        toast.error('An application with this email already exists.');
      } else {
        toast.error(error.message || 'Failed to save details. Please try again.');
      }
    }
  };

  // ✅ NEW: Open Paystack popup
  const openPaystackPopup = (reference, onSuccess, onClose) => {
    return new Promise((resolve, reject) => {
      if (!window.PaystackPop) return reject(new Error('Paystack not loaded'));

      const config = {
        key: PAYSTACK_PUBLIC_KEY,
        email: personalDetails.personalEmail,
        amount: premium * 100, // in kobo
        currency: 'NGN',
        ref: reference,
        meta: {
          company_id: companyId,
          link_id: linkId,
          product_id: productId,
          agent_id: agentId,  // ✅ Include agent in metadata
          period_months: selectedPeriod?.months,
          applicant_name: personalDetails.personalName,
          applicant_phone: personalDetails.personalPhone,
          product_name: productDetails?.Name || productDetails?.name
        },
        callback: function(response) {
          resolve(response);
          onSuccess?.(response);
        },
        onClose: function() {
          reject(new Error('Payment cancelled'));
          onClose?.();
        }
      };

      window.PaystackPop.setup(config).openIframe();
    });
  };

  // ✅ NEW: Main payment handler
  const handlePayment = async () => {
    if (!selectedPeriod) return toast.warning('Please select a period of cover');
    if (!paystackLoaded) return toast.warning('Payment gateway is loading. Please wait...');
    if (!personalDetails.personalEmail) return toast.warning('Email is required for payment');
    if (productLoading) return toast.warning('Loading product details. Please wait...');

    setLoading(true);
    const reference = generateReference();

    try {
      await openPaystackPopup(reference, handlePaymentSuccess, () => toast.info('Payment window closed'));
    } catch (error) {
      if (error.message === 'Payment cancelled') {
        toast.info('Payment cancelled by user');
      } else {
        console.error('💥 Payment error:', error);
        toast.error(error.message || 'Payment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Submit Handler - Now triggers payment flow
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // ✅ Instead of just saving, now initiate payment
    await handlePayment();
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/');
    }
  };

  // ✅ Helper: Render gender dropdown options
  const renderGenderOptions = () => {
    if (gendersLoading) {
      return <option value="">Loading...</option>;
    }
    
    return (
      <>
        <option value="">Select gender</option>
        {genderOptions.map(gender => (
          <option key={gender.id} value={gender.id}>
            {gender.name}
          </option>
        ))}
      </>
    );
  };

  // ✅ Helper: Render period dropdown options
  const renderPeriodOptions = () => {
    if (productLoading) {
      return <div className="p-3 text-muted">Loading plans...</div>;
    }
    
    return paymentPeriods.map((period) => (
      <div 
        key={period.value} 
        onClick={() => !loading && !productLoading && handlePeriodSelect(period)} 
        className="p-3" 
        style={{ 
          cursor: (loading || productLoading) ? 'not-allowed' : 'pointer', 
          backgroundColor: selectedPeriod?.value === period.value ? INFO_BG : 'white', 
          color: selectedPeriod?.value === period.value ? PRIMARY_COLOR : '#111827', 
          fontSize: '14px', 
          borderBottom: '1px solid #f3f4f6' 
        }}
      >
        {period.label} - {formatPrice(basePremiumPerMonth * period.months)}
      </div>
    ));
  };

  // ✅ Helper: Render premium info
  const renderPremiumInfo = () => {
    if (productLoading) {
      return (
        <div className="text-muted small">
          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          Loading premium...
        </div>
      );
    }
    
    if (productDetails) {
      return (
        <small className="text-muted d-block mt-1">
          {formatPrice(basePremiumPerMonth)} × {selectedPeriod?.months} month{selectedPeriod?.months > 1 ? 's' : ''}
          {productDetails.Name && ` • ${productDetails.Name}`}
        </small>
      );
    }
    
    return (
      <small className="text-muted d-block mt-1">
        {formatPrice(basePremiumPerMonth)} × {selectedPeriod?.months} month{selectedPeriod?.months > 1 ? 's' : ''}
      </small>
    );
  };

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer position="top-center" autoClose={3000} />
      
      {/* Header */}
      <header className="bg-white border-bottom py-3">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <img src="/images/fsl_logo.png" alt="FSL Logo" style={{ height: '40px' }} />
            </div>
            <button className="btn btn-light rounded-circle" style={{ width: '36px', height: '36px' }} onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow-1 py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              
              {/* Page Title */}
              <div className="text-center mb-4">
                <h1 className="fw-bold" style={{ fontSize: '24px', color: '#111827' }}>
                  Provide plan owner details
                </h1>
                {agentId && (
                  <div className="mt-2">
                    <span className="badge" style={{ backgroundColor: `${PRIMARY_COLOR}15`, color: PRIMARY_COLOR, border: `1px solid ${INFO_BORDER}`, padding: '6px 12px', fontSize: '12px', fontWeight: '500' }}>
                      <i className="fas fa-user-tag me-1"></i>
                      Agent: {agentId.substring(0, 8)}...
                    </span>
                  </div>
                )}
              </div>

              {/* Form Card */}
              <div className="rounded-3 p-4" style={{ backgroundColor: 'white' }}>
                
                {/* Info Alert */}
                <div className="rounded-3 p-3 mb-4 d-flex align-items-start gap-3" style={{ backgroundColor: INFO_BG, border: `1px solid ${INFO_BORDER}` }}>
                  <i className="fas fa-info-circle mt-1" style={{ color: INFO_ICON, fontSize: '18px', flexShrink: 0 }}></i>
                  <p className="mb-0" style={{ fontSize: '14px', color: INFO_TEXT }}>
                    Submit the correct details as they appear on legal documents.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  
                  {/* ✅ SECTION 1: Personal Details */}
                  <div className="mb-4 pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <h5 className="fw-semibold mb-3" style={{ color: PRIMARY_COLOR }}>
                      <i className="fas fa-user me-2"></i>Personal Details
                    </h5>
                    
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                          Full Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="personalName"
                          className="form-control"
                          placeholder="Enter your full name"
                          value={personalDetails.personalName}
                          onChange={handlePersonalChange}
                          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                          required
                          disabled={loading || gendersLoading || productLoading}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          name="personalEmail"
                          className="form-control"
                          placeholder="Enter your email"
                          value={personalDetails.personalEmail}
                          onChange={handlePersonalChange}
                          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                          required
                          disabled={loading || gendersLoading || productLoading}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                          Phone Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          name="personalPhone"
                          className="form-control"
                          placeholder="Enter your phone number"
                          value={personalDetails.personalPhone}
                          onChange={handlePersonalChange}
                          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                          required
                          disabled={loading || gendersLoading || productLoading}
                          pattern="^[0-9+\s\-()]{10,}$"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                          Date of Birth <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          className="form-control"
                          value={personalDetails.dateOfBirth}
                          onChange={handlePersonalChange}
                          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                          required
                          disabled={loading || gendersLoading || productLoading}
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                          Gender <span className="text-danger">*</span>
                        </label>
                        <select
                          name="gender"
                          className="form-select"
                          value={personalDetails.gender}
                          onChange={handlePersonalChange}
                          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                          required
                          disabled={loading || gendersLoading || productLoading}
                        >
                          {renderGenderOptions()}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* ✅ SECTION 2: Spouse Details (Optional) */}
                  <div className="mb-4 pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="fw-semibold mb-0" style={{ color: PRIMARY_COLOR }}>
                        <i className="fas fa-user-friends me-2"></i>Spouse Details <span className="text-muted fw-normal">(Optional)</span>
                      </h5>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="hasSpouse"
                          checked={hasSpouse}
                          onChange={(e) => setHasSpouse(e.target.checked)}
                          disabled={loading || gendersLoading || productLoading}
                          style={{ accentColor: PRIMARY_COLOR }}
                        />
                        <label className="form-check-label" htmlFor="hasSpouse" style={{ fontSize: '14px' }}>
                          Add spouse
                        </label>
                      </div>
                    </div>

                    {hasSpouse && (
                      <>
                        <div className="row">
                          <div className="col-12 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                              Spouse Full Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              name="spouseName"
                              className="form-control"
                              placeholder="Enter spouse full name"
                              value={spouseDetails.spouseName}
                              onChange={handleSpouseChange}
                              style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                              required={hasSpouse}
                              disabled={loading || gendersLoading || productLoading}
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                              Spouse Gender <span className="text-danger">*</span>
                            </label>
                            <select
                              name="spouseGender"
                              className="form-select"
                              value={spouseDetails.spouseGender}
                              onChange={handleSpouseChange}
                              style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                              required={hasSpouse}
                              disabled={loading || gendersLoading || productLoading}
                            >
                              {renderGenderOptions()}
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                              Spouse Email
                            </label>
                            <input
                              type="email"
                              name="spouseEmail"
                              className="form-control"
                              placeholder="Enter spouse email (optional)"
                              value={spouseDetails.spouseEmail}
                              onChange={handleSpouseChange}
                              style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                              disabled={loading || gendersLoading || productLoading}
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                              Spouse Phone
                            </label>
                            <input
                              type="tel"
                              name="spousePhone"
                              className="form-control"
                              placeholder="Enter spouse phone (optional)"
                              value={spouseDetails.spousePhone}
                              onChange={handleSpouseChange}
                              style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                              disabled={loading || gendersLoading || productLoading}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                              Spouse Date of Birth <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              name="spouseDateOfBirth"
                              className="form-control"
                              value={spouseDetails.spouseDateOfBirth}
                              onChange={handleSpouseChange}
                              style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
                              required={hasSpouse}
                              disabled={loading || gendersLoading || productLoading}
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* ✅ SECTION 3: Dependents (Multiple) */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="fw-semibold mb-0" style={{ color: PRIMARY_COLOR }}>
                        <i className="fas fa-users me-2"></i>Dependents
                      </h5>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={addDependent}
                        disabled={loading || gendersLoading || productLoading}
                        style={{ backgroundColor: PRIMARY_COLOR, color: 'white', border: 'none', padding: '6px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '500' }}
                      >
                        <i className="fas fa-plus me-1"></i>Add Dependent
                      </button>
                    </div>

                    {dependents.map((dependent, index) => (
                      <div key={dependent.id} className="rounded-3 p-3 mb-3" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-semibold mb-0" style={{ fontSize: '15px' }}>
                            Dependent {index + 1}
                          </h6>
                          {dependents.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm text-danger"
                              onClick={() => removeDependent(dependent.id)}
                              disabled={loading || gendersLoading || productLoading}
                              style={{ background: 'none', border: 'none', padding: '4px 8px', fontSize: '12px' }}
                            >
                              <i className="fas fa-trash me-1"></i>Remove
                            </button>
                          )}
                        </div>

                        <div className="row">
                          <div className="col-12 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: '13px', color: '#111827' }}>
                              Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter dependent name"
                              value={dependent.name}
                              onChange={(e) => handleDependentChange(dependent.id, 'name', e.target.value)}
                              style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '14px' }}
                              required
                              disabled={loading || gendersLoading || productLoading}
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: '13px', color: '#111827' }}>
                              Gender <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={dependent.gender}
                              onChange={(e) => handleDependentChange(dependent.id, 'gender', e.target.value)}
                              style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '14px' }}
                              required
                              disabled={loading || gendersLoading || productLoading}
                            >
                              {renderGenderOptions()}
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: '13px', color: '#111827' }}>
                              Relationship <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={dependent.relationship}
                              onChange={(e) => handleDependentChange(dependent.id, 'relationship', e.target.value)}
                              style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '14px' }}
                              required
                              disabled={loading || gendersLoading || productLoading}
                            >
                              <option value="">Select relationship</option>
                              <option value="Child">Child</option>
                              <option value="Spouse">Spouse</option>
                              <option value="Parent">Parent</option>
                              <option value="Sibling">Sibling</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ✅ NEW SECTION 4: Coverage Period & Premium */}
                  <div className="mb-4 pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <h5 className="fw-semibold mb-3" style={{ color: PRIMARY_COLOR }}>
                      <i className="fas fa-calendar-alt me-2"></i>Coverage & Payment
                    </h5>
                    
                    {/* Period Selection */}
                    <div className="mb-3 period-dropdown">
                      <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                        Period Of Cover <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <div 
                          onClick={() => !loading && !productLoading && setShowPeriodDropdown(!showPeriodDropdown)} 
                          className="form-control" 
                          style={{ 
                            backgroundColor: (loading || productLoading) ? '#f3f4f6' : 'white', 
                            border: `2px solid ${showPeriodDropdown ? PRIMARY_COLOR : '#E5E7EB'}`, 
                            borderRadius: '8px', 
                            padding: '10px 12px', 
                            cursor: (loading || productLoading) ? 'not-allowed' : 'pointer', 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            opacity: (loading || productLoading) ? 0.7 : 1 
                          }}
                        >
                          <span style={{ color: selectedPeriod ? '#111827' : '#6b7280' }}>
                            {selectedPeriod ? selectedPeriod.label : (productLoading ? 'Loading plans...' : 'Select payment plan')}
                          </span>
                          <i className={`fas fa-chevron-${showPeriodDropdown ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                        </div>

                        {showPeriodDropdown && (
                          <div className="position-absolute w-100 mt-1 rounded-3 shadow" style={{ backgroundColor: 'white', border: `1px solid #E5E7EB`, zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>
                            {renderPeriodOptions()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Premium Display */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold" style={{ fontSize: '14px', color: '#111827' }}>
                        Premium
                      </label>
                      <div className="rounded-3 p-3" style={{ backgroundColor: PREMIUM_BG, border: `1px solid ${INFO_BORDER}` }}>
                        <h4 className="mb-0 fw-bold" style={{ color: PREMIUM_TEXT }}>
                          {premium > 0 ? formatPrice(premium) : '₦0'}
                        </h4>
                        {premium > 0 && renderPremiumInfo()}
                      </div>
                      {/* ✅ Show product name if loaded */}
                      {productDetails?.Name && (
                        <small className="text-muted d-block mt-2">
                          Product: <strong>{productDetails.Name}</strong>
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Submit/Pay Button */}
                  <button
                    type="submit"
                    disabled={loading || gendersLoading || productLoading || !selectedPeriod || !paystackLoaded}
                    className="btn w-100 py-3 fw-semibold rounded-3 mt-4"
                    style={{
                      backgroundColor: PRIMARY_COLOR,
                      color: 'white',
                      border: 'none',
                      transition: 'all 0.3s ease',
                      opacity: (loading || gendersLoading || productLoading || !selectedPeriod || !paystackLoaded) ? 0.7 : 1,
                      cursor: (loading || gendersLoading || productLoading || !selectedPeriod || !paystackLoaded) ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => { if (!loading && !gendersLoading && !productLoading && selectedPeriod && paystackLoaded) e.target.style.backgroundColor = PRIMARY_HOVER; }}
                    onMouseLeave={(e) => { if (!loading && !gendersLoading && !productLoading && selectedPeriod && paystackLoaded) e.target.style.backgroundColor = PRIMARY_COLOR; }}
                  >
                    {loading ? (
                      <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Processing Payment...</>
                    ) : !paystackLoaded ? (
                      <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Loading Payment...</>
                    ) : productLoading ? (
                      <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Loading Premium...</>
                    ) : !selectedPeriod ? (
                      'Select Coverage Period'
                    ) : (
                      <><i className="fas fa-lock me-2"></i>Pay {formatPrice(premium)}</>
                    )}
                  </button>

                  <p className="text-center text-muted small mt-3 mb-0">
                    <i className="fas fa-shield-alt me-1" style={{ color: PRIMARY_COLOR }}></i> Secured by Paystack • 256-bit SSL Encryption
                  </p>

                </form>
              </div>

            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-top py-3">
        <div className="container text-center">
          <p className="text-muted small mb-0">
            <i className="fas fa-shield-alt me-1" style={{ color: PRIMARY_COLOR }}></i>
            Powered by <span style={{ fontWeight: '600', color: PRIMARY_COLOR }}>FSL</span>
          </p>
        </div>
      </footer>

      {/* Overlay to close period dropdown */}
      {showPeriodDropdown && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100" 
          style={{ zIndex: 999, backgroundColor: 'transparent' }} 
          onClick={() => setShowPeriodDropdown(false)} 
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default PlanOwnerDetails;